import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconAlertCircle,
  IconArrowUp,
  IconArrowsMaximize,
  IconCheck,
  IconCloudCheck,
  IconDownload,
  IconGripVertical,
  IconHome,
  IconLoader2,
  IconPencil,
  IconPlayerPlay,
  IconRefresh,
  IconSparkles,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { ApiError, userApi } from "./api";
import { GenerationModeTabs } from "./generation-mode-tabs";
import type { ProjectDetailResponse } from "./project-types";
import { notifyCreditActionSubmitted, requestCreditSpend } from "./credit-interactions";
import { THEME_PREVIEW_IMAGES, themeOptionsForMode } from "./theme-options";
import { downloadPptxUrl, ensureBrowserImagePptx } from "./pptx-export";
import { useImageThemeOptions } from "./image-theme-catalog";
import { useUserAuth } from "./auth";
import { capabilityEnabled, featureEnabled } from "./feature-access";
import { AdvancedBeautifyAnnotation } from "./advanced-beautify-annotation";
import {
  MAX_ADVANCED_BEAUTIFY_REGIONS,
} from "./advanced-beautify-annotation-utils";
import {
  IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH,
  image2RevisionInstructionLength,
  image2RevisionInstructionValid,
  limitImage2RevisionInstruction,
} from "./image2-revision-instruction";
import { userFacingGenerationMessage } from "./user-facing-generation-message";
import {
  latestRollbackVersionBySlide,
  useImage2RevisionSession,
  visibleImage2RevisionMessages,
  type Image2RevisionFeedback,
  type Image2RevisionJob,
  type Image2RevisionJobStatus,
  type PptEditMessage,
} from "./image2-revision-session";

export {
  IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH as ADVANCED_REVISION_INSTRUCTION_MAX_LENGTH,
  IMAGE2_REVISION_INSTRUCTION_MIN_LENGTH as ADVANCED_REVISION_INSTRUCTION_MIN_LENGTH,
  image2RevisionInstructionLength as advancedRevisionInstructionLength,
  image2RevisionInstructionValid as advancedRevisionInstructionValid,
  limitImage2RevisionInstruction as limitAdvancedRevisionInstruction,
} from "./image2-revision-instruction";

export type AdvancedBeautifyStatus = "idle" | "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type AdvancedBeautifySlideStatus = "waiting" | "running" | "succeeded" | "failed";
export type AdvancedBeautifyMode = "enhanced" | "premium" | "faithful";
export type AdvancedBeautifyThemeCode = string;
export type AdvancedBeautifyStartAction = "initial" | "resume";
export type MissingSlideGenerationStep = "confirming" | "submitting" | "submitted" | "error";

export interface AdvancedBeautifySelection {
  beautifyMode: AdvancedBeautifyMode;
  themeCode: AdvancedBeautifyThemeCode;
}

interface AdvancedBeautifyStartOptions {
  action: AdvancedBeautifyStartAction;
  policy?: AdvancedBeautifySelection;
  standardPreviewFrame?: HTMLIFrameElement | null;
  idempotencyKey?: string;
}

const ADVANCED_BEAUTIFY_MODE_OPTIONS: Array<{
  value: AdvancedBeautifyMode;
  name: string;
  description: string;
  recommended?: boolean;
}> = [
  { value: "enhanced", name: "高质量增强", description: "基于文字大纲和本页图片素材进行高质量重构；允许优化文字并部分重绘非事实性图片。", recommended: true },
  { value: "premium", name: "素材锁定", description: "基于文字大纲直接使用本页原始图片；文字和图片均禁止重写、重绘或替换。" },
  { value: "faithful", name: "保真美化", description: "保持原页构图意图和内容关系，重点优化字体、间距、对齐与配色。" },
];

export function advancedBeautifyStartRequest(input: {
  action: AdvancedBeautifyStartAction;
  policy: AdvancedBeautifySelection;
  idempotencyKey?: string;
}) {
  return {
    beautifyMode: input.policy.beautifyMode,
    themeCode: input.policy.themeCode,
    ...(input.action === "resume" ? { resume: true } : {}),
    ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
  };
}

export function advancedBeautifyResumeCreditNotice(
  action: AdvancedBeautifyStartAction,
  status: AdvancedBeautifyStatus | undefined,
): string | undefined {
  if (action !== "resume" || status !== "failed") return undefined;
  return "上次高级美化失败后，已扣积分已自动退回。本次显示的是重新预扣，不是重复扣费；成功后整套任务最终只净扣一次，如再次失败仍会自动退回。";
}

export function advancedBeautifyVisibleMessages<T extends { role: string }>(messages: readonly T[]): T[] {
  return visibleImage2RevisionMessages(messages);
}

export interface MissingSlideGenerationFlow extends AdvancedBeautifySelection {
  id: string;
  idempotencyKey: string;
  slideIndex: number;
  slideTitle: string;
  step: MissingSlideGenerationStep;
  error?: string;
  requestId?: string;
}

interface MissingSlideGenerationResponse {
  ok: true;
  queued: true;
  requestMessage?: PptEditMessage;
  progressMessage?: PptEditMessage;
}

export function reorderAdvancedBeautifySlides<T>(slides: readonly T[], fromIndex: number, toIndex: number): T[] {
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)
    || fromIndex < 0 || toIndex < 0 || fromIndex >= slides.length || toIndex >= slides.length
    || fromIndex === toIndex) return [...slides];
  const reordered = [...slides];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  return reordered;
}

export function preserveAdvancedBeautifyGeneratedSlides<T extends {
  index: number;
  sourceSlideId?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
}>(previousSlides: readonly T[], nextSlides: readonly T[]): T[] {
  const previousBySourceId = new Map(
    previousSlides.filter((slide) => slide.sourceSlideId).map((slide) => [slide.sourceSlideId, slide] as const),
  );
  const previousByIndex = new Map(previousSlides.map((slide) => [slide.index, slide] as const));
  return nextSlides.map((slide) => {
    if (slide.imageUrl || slide.thumbnailUrl) return slide;
    const previous = (slide.sourceSlideId && previousBySourceId.get(slide.sourceSlideId))
      || previousByIndex.get(slide.index);
    if (!previous?.imageUrl && !previous?.thumbnailUrl) return slide;
    return { ...slide, imageUrl: previous.imageUrl, thumbnailUrl: previous.thumbnailUrl };
  });
}

export function shouldPromptAdvancedBeautifySlideGeneration(input: {
  status: AdvancedBeautifyStatus;
  slide: {
    status: AdvancedBeautifySlideStatus;
    imageUrl?: string;
    thumbnailUrl?: string;
  };
}): boolean {
  return ["succeeded", "failed", "cancelled"].includes(input.status)
    && ["waiting", "failed"].includes(input.slide.status)
    && !input.slide.imageUrl
    && !input.slide.thumbnailUrl;
}

export function advancedBeautifyGenerationSubmissionBlocked(
  status: AdvancedBeautifyStatus,
  busy = "",
): boolean {
  return ["queued", "running"].includes(status)
    || ["start", "resume", "retry", "missing-slide"].includes(busy);
}

interface AdvancedBeautifyResponse {
  ok: true;
  mode: "idle" | "existing" | "created" | "queued" | "running" | "succeeded" | "failed" | "cancelled";
  task: { id: string; title: string; status: string; progressPercent: number; progressText?: string };
  standardPreviewUrl?: string;
  standardRenderId?: string;
  standardPptxReady?: boolean;
  advancedBeautify: {
    renderId?: string;
    renderer: "image2";
    productCode: "advanced-beautify-image2";
    status: AdvancedBeautifyStatus;
    completedSlides: number;
    totalSlides: number;
    currentSlide?: number;
    previewUrl?: string;
    downloadUrl?: string;
    errorMessage?: string;
    policy?: {
      schemaVersion: "advanced-beautify-policy/v1" | "advanced-beautify-policy/v2";
      beautifyMode: AdvancedBeautifyMode;
      themeCode: AdvancedBeautifyThemeCode;
      workflowVersion?: string;
      referenceStrategy?: "semantic_assets" | "standard_slide";
      textPolicy?: "rewrite_with_facts_locked" | "exact";
      assetPolicy?: "redraw_allowed" | "locked" | "standard_page";
    };
    revisionUndo?: Array<{ slideIndex: number; instruction: string; confirmedAt: string }>;
    slides: Array<{
      index: number;
      sourceSlideId?: string;
      title: string;
      status: AdvancedBeautifySlideStatus;
      imageUrl?: string;
      thumbnailUrl?: string;
      fallbackThumbnailUrl?: string;
      progressText?: string;
      errorMessage?: string;
    }>;
  };
}

function preserveAdvancedBeautifyGeneratedImages(
  previous: AdvancedBeautifyResponse | undefined,
  next: AdvancedBeautifyResponse,
): AdvancedBeautifyResponse {
  if (!previous) return next;
  return {
    ...next,
    advancedBeautify: {
      ...next.advancedBeautify,
      slides: preserveAdvancedBeautifyGeneratedSlides(
        previous.advancedBeautify.slides,
        next.advancedBeautify.slides,
      ),
    },
  };
}

const ADVANCED_BEAUTIFY_CACHE_TTL_MS = 15 * 60 * 1000;
const ADVANCED_REVISION_INSTRUCTION_MAX_LENGTH = IMAGE2_REVISION_INSTRUCTION_MAX_LENGTH;
const advancedBeautifyMemoryCache = new Map<string, { savedAt: number; value: AdvancedBeautifyResponse }>();

const advancedRevisionInstructionLength = image2RevisionInstructionLength;
const advancedRevisionInstructionValid = image2RevisionInstructionValid;
const limitAdvancedRevisionInstruction = limitImage2RevisionInstruction;

export function AdvancedBeautifyPage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const auth = useUserAuth();
  const imageModeAvailable = featureEnabled(auth.me?.features, "renderer.image2")
    && capabilityEnabled(auth.me?.features, "generation.advanced_beautify");
  const [detail, setDetail] = useState<ProjectDetailResponse | undefined>(() => loadSessionProjectDetail(taskId));
  const [data, setData] = useState<AdvancedBeautifyResponse | undefined>(() => loadCachedAdvancedBeautify(taskId));
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [requestId, setRequestId] = useState("");
  const [busy, setBusy] = useState("");
  const [beautifyMode, setBeautifyMode] = useState<AdvancedBeautifyMode>(
    data?.advancedBeautify.policy?.beautifyMode || "enhanced",
  );
  const [themeCode, setThemeCode] = useState<AdvancedBeautifyThemeCode>(
    data?.advancedBeautify.policy?.themeCode || "smart-detect",
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [missingSlideGeneration, setMissingSlideGeneration] = useState<MissingSlideGenerationFlow>();
  const [draggedSlideIndex, setDraggedSlideIndex] = useState<number>();
  const [dragOverSlideIndex, setDragOverSlideIndex] = useState<number>();
  const stageRef = useRef<HTMLElement>(null);
  const userSelectedSlide = useRef(false);

  const loadProjectDetail = useCallback(async () => {
    const project = await userApi<ProjectDetailResponse>(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}`);
    cacheSessionProjectDetail(taskId, project);
    setDetail(project);
    return project;
  }, [taskId]);

  const loadStatus = useCallback(async () => {
    const next = await userApi<AdvancedBeautifyResponse>(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/advanced-beautify`);
    setData((current) => {
      const visibleNext = preserveAdvancedBeautifyGeneratedImages(current, next);
      cacheAdvancedBeautify(taskId, visibleNext);
      return visibleNext;
    });
    setError("");
    setErrorCode("");
    setRequestId("");
    return next;
  }, [taskId]);

  const start = useCallback(async (options: AdvancedBeautifyStartOptions): Promise<{ submitted: boolean; error?: string }> => {
    if (!imageModeAvailable) {
      const message = "高级美化当前未向此账户开放";
      setError(message);
      setErrorCode("feature_not_available");
      return { submitted: false, error: message };
    }
    if (advancedBeautifyGenerationSubmissionBlocked(data?.advancedBeautify.status || "idle", busy)) {
      const message = "当前已有图片生成任务，请等待任务结束后再提交";
      setError(message);
      return { submitted: false, error: message };
    }
    const resume = options.action === "resume";
    const startingFresh = options.action === "initial";
    const selectedPolicy = options.policy || { beautifyMode, themeCode };
    try {
      const approved = await requestCreditSpend({
        operation: "image2_generate",
        slideCount: data?.advancedBeautify.totalSlides || detail?.deckVersion?.document.slides.length || 1,
        billingNotice: advancedBeautifyResumeCreditNotice(options.action, data?.advancedBeautify.status),
      });
      if (!approved) return { submitted: false };
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "积分信息加载失败，请稍后重试";
      setError(message);
      return { submitted: false, error: message };
    }
    setBusy(resume ? "resume" : "start");
    setError("");
    setErrorCode("");
    setRequestId("");
    if (advancedBeautifyRequiresStandardPptx(selectedPolicy.beautifyMode) && !data?.standardPptxReady) {
      try {
        if (!data?.standardRenderId) throw new Error("找不到标准模式渲染结果，请返回标准模式重新生成。");
        await ensureBrowserImagePptx(data.standardRenderId, options.standardPreviewFrame);
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : "标准页面高清图片准备失败";
        setBusy("");
        setError(message);
        return { submitted: false, error: message };
      }
    }
    setData((current) => current ? {
        ...current,
        mode: "running",
        task: {
          ...current.task,
          progressPercent: resume ? current.task.progressPercent : startingFresh ? 0 : 1,
          progressText: resume
            ? `继续生成第 ${Math.max(1, current.advancedBeautify.currentSlide || current.advancedBeautify.completedSlides + 1)} 页`
            : "正在启动高级美化，准备生成第 1 页",
        },
        advancedBeautify: {
          ...current.advancedBeautify,
          status: "running",
          currentSlide: resume ? current.advancedBeautify.currentSlide || current.advancedBeautify.completedSlides + 1 : 1,
          completedSlides: resume ? current.advancedBeautify.completedSlides : 0,
          slides: current.advancedBeautify.slides.map((slide) => resume
            ? slide
            : { ...slide, status: slide.index === 1 ? "running" as const : "waiting" as const, progressText: slide.index === 1 ? "正在准备并生成第 1 页" : undefined, errorMessage: undefined }),
        },
      } : current);
    userSelectedSlide.current = false;
    try {
      const next = await userApi<AdvancedBeautifyResponse>(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/advanced-beautify`, {
        method: "POST",
        body: JSON.stringify(advancedBeautifyStartRequest({
          action: options.action,
          policy: selectedPolicy,
          idempotencyKey: options.idempotencyKey,
        })),
      });
      notifyCreditActionSubmitted();
      setData((current) => {
        const visibleNext = preserveAdvancedBeautifyGeneratedImages(current, next);
        cacheAdvancedBeautify(taskId, visibleNext);
        return visibleNext;
      });
      setError("");
      setErrorCode("");
      setRequestId("");
      return { submitted: true };
    } catch (caught) {
      if (startingFresh) {
        setData((current) => current ? {
          ...current,
          mode: "idle",
          task: { ...current.task, progressPercent: 0, progressText: undefined },
          advancedBeautify: {
            ...current.advancedBeautify,
            status: "idle",
            currentSlide: undefined,
            completedSlides: 0,
            slides: current.advancedBeautify.slides.map((slide) => ({ ...slide, status: "waiting", progressText: undefined })),
          },
        } : current);
      }
      const message = caught instanceof Error ? caught.message : "高级美化启动失败";
      setError(message);
      setErrorCode(caught instanceof ApiError ? caught.code || "" : "");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      return { submitted: false, error: message };
    } finally {
      setBusy("");
    }
  }, [
    beautifyMode,
    busy,
    data,
    data?.advancedBeautify.totalSlides,
    data?.standardPptxReady,
    data?.standardRenderId,
    detail?.deckVersion?.document.slides.length,
    imageModeAvailable,
    taskId,
    themeCode,
  ]);

  useEffect(() => {
    const policy = data?.advancedBeautify.policy;
    if (!policy || data?.advancedBeautify.status === "idle") return;
    setBeautifyMode(policy.beautifyMode);
    setThemeCode(policy.themeCode);
  }, [data?.advancedBeautify.policy, data?.advancedBeautify.status]);

  useEffect(() => {
    let cancelled = false;
    void loadStatus().catch((caught) => {
      if (cancelled) return;
      setError(caught instanceof Error ? caught.message : "高级美化页面加载失败");
      setErrorCode(caught instanceof ApiError ? caught.code || "" : "");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    });
    return () => { cancelled = true; };
  }, [loadStatus]);

  useEffect(() => {
    if (!data?.advancedBeautify.renderId) return;
    void loadProjectDetail().catch((caught) => {
      console.warn("Advanced beautify project detail refresh failed; status remains authoritative.", caught);
    });
  }, [data?.advancedBeautify.renderId, loadProjectDetail]);

  useEffect(() => {
    const status = data?.advancedBeautify.status;
    if (!status || !["queued", "running"].includes(status)) return;
    if (busy === "start") return;
    const timer = window.setInterval(() => {
      void loadStatus().catch((caught) => {
        setError(caught instanceof Error ? caught.message : "高级美化状态刷新失败");
        setErrorCode(caught instanceof ApiError ? caught.code || "" : "");
        setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [busy, data?.advancedBeautify.status, loadStatus]);

  const slides = data?.advancedBeautify.slides || [];
  const selectedSlide = slides[selectedIndex] || slides[0];
  const selectedDeckSlide = detail?.deckVersion?.document.slides.find((slide) => slide.id === selectedSlide?.sourceSlideId)
    || detail?.deckVersion?.document.slides[selectedIndex];
  const progressPercent = Math.max(0, Math.min(100, Math.round(data?.task.progressPercent || 0)));
  const status = data?.advancedBeautify.status || "idle";
  const canPoll = status === "queued" || status === "running";
  const canResume = shouldShowAdvancedBeautifyResume({
    status,
    completedSlides: data?.advancedBeautify.completedSlides || 0,
    totalSlides: data?.advancedBeautify.totalSlides || 0,
  });
  const previewImage = selectedSlide?.imageUrl || selectedSlide?.fallbackThumbnailUrl || "";
  const selectedPageNumber = Math.max(1, selectedSlide?.index || selectedDeckSlide?.index || selectedIndex + 1);
  useEffect(() => {
    if (!advancedBeautifyGenerationSubmissionBlocked(status, busy)) return;
    setMissingSlideGeneration((current) => current?.step === "confirming" ? undefined : current);
  }, [busy, status]);
  const sourceRender = detail?.renders.find((render) => render.id === data?.advancedBeautify.renderId);
  const refreshRevisionSource = useCallback(async () => {
    await Promise.all([loadStatus(), loadProjectDetail()]);
  }, [loadProjectDetail, loadStatus]);
  const revision = useImage2RevisionSession({
    render: sourceRender,
    selectedPage: selectedPageNumber,
    enabled: status === "succeeded" && Boolean(previewImage),
    idempotencyPrefix: "advanced",
    initialAnnotationMode: true,
    preserveMessagesAcrossRenders: true,
    onRefresh: refreshRevisionSource,
    onSelectPage: (page) => {
      userSelectedSlide.current = true;
      setSelectedIndex(Math.max(0, page - 1));
    },
  });
  const selectedRegions = revision.regions;
  useEffect(() => {
    setSelectedIndex((previous) => chooseAdvancedBeautifyDisplayIndex({
      previousIndex: previous,
      slides,
      status,
      currentSlide: data?.advancedBeautify.currentSlide,
      userSelected: userSelectedSlide.current,
    }));
  }, [data?.advancedBeautify.currentSlide, slides, status]);

  const pageList = useMemo<AdvancedBeautifyResponse["advancedBeautify"]["slides"]>(() => slides.length ? slides : Array.from({ length: detail?.deckVersion?.document.slides.length || 0 }, (_, index) => ({
    index: index + 1,
    title: detail?.deckVersion?.document.slides[index]?.title || `第 ${index + 1} 页`,
    status: "waiting" as const,
  })), [detail?.deckVersion?.document.slides, slides]);
  const missingSlideStatus = missingSlideGeneration && missingSlideGeneration.step !== "confirming"
    ? pageList.find((slide) => slide.index === missingSlideGeneration.slideIndex)?.status
    : undefined;
  useEffect(() => {
    if (missingSlideGeneration?.step !== "submitted" || canPoll || missingSlideStatus !== "succeeded") return;
    void loadProjectDetail().catch((caught) => {
      console.warn("Advanced beautify missing-slide detail refresh failed; status remains authoritative.", caught);
    });
  }, [canPoll, loadProjectDetail, missingSlideGeneration?.id, missingSlideGeneration?.step, missingSlideStatus]);
  const selectSlide = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, Math.max(0, pageList.length - 1)));
    userSelectedSlide.current = true;
    setSelectedIndex(safeIndex);
    const slide = pageList[safeIndex];
    const missingSlideRequestActive = missingSlideGeneration?.step === "submitting"
      || (missingSlideGeneration?.step === "submitted" && ["queued", "running"].includes(status));
    if (!slide || busy || revision.processing
      || missingSlideRequestActive
      || !shouldPromptAdvancedBeautifySlideGeneration({ status, slide })) return;
    const policy = data?.advancedBeautify.policy || { beautifyMode, themeCode };
    const flow: MissingSlideGenerationFlow = {
      id: crypto.randomUUID(),
      idempotencyKey: `advanced-missing-slide-${slide.index}-${crypto.randomUUID()}`,
      slideIndex: slide.index,
      slideTitle: slide.title,
      beautifyMode: policy.beautifyMode,
      themeCode: policy.themeCode,
      step: "confirming",
    };
    setMissingSlideGeneration(flow);
  };

  const confirmMissingSlideGeneration = () => {
    const flow = missingSlideGeneration;
    if (!flow || flow.step !== "confirming" || advancedBeautifyGenerationSubmissionBlocked(status, busy)) return;
    setMissingSlideGeneration({ ...flow, step: "submitting", error: undefined, requestId: undefined });
    setError("");
    setErrorCode("");
    setRequestId("");
    setBusy("missing-slide");
    void (async () => {
      try {
        const approved = await requestCreditSpend({
        operation: "image2_generate",
        slideCount: 1,
      });
        if (!approved) {
          setMissingSlideGeneration((current) => current?.id === flow.id
            ? { ...current, step: "confirming" }
            : current);
          return;
        }
        await userApi<MissingSlideGenerationResponse>(
          `/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/advanced-beautify/slides/${flow.slideIndex}/generate`,
          {
            method: "POST",
            body: JSON.stringify({ idempotencyKey: flow.idempotencyKey }),
          },
        );
        notifyCreditActionSubmitted();
        setMissingSlideGeneration((current) => current?.id === flow.id
          ? { ...current, step: "submitted", error: undefined }
          : current);
        await loadStatus();
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : "页面图片生成请求提交失败";
        setMissingSlideGeneration((current) => current?.id === flow.id
          ? {
              ...current,
              step: "error",
              error: message,
              requestId: caught instanceof ApiError ? caught.requestId || undefined : undefined,
            }
          : current);
      } finally {
        setBusy("");
      }
    })();
  };

  const cancelMissingSlideGeneration = () => {
    setMissingSlideGeneration((current) => current?.step === "confirming" ? undefined : current);
  };

  const reorderSlide = async (fromIndex: number, toIndex: number) => {
    if (busy || status !== "succeeded" || fromIndex === toIndex || !data?.advancedBeautify.renderId) return;
    const reordered = reorderAdvancedBeautifySlides(pageList, fromIndex, toIndex);
    const slideOrder = reordered.map((slide) => slide.index);
    const selected = pageList[selectedIndex];
    const nextSelectedIndex = Math.max(0, reordered.indexOf(selected));
    const previousData = data;
    userSelectedSlide.current = true;
    setSelectedIndex(nextSelectedIndex);
    setBusy("reorder");
    setError("");
    setData((current) => current ? {
      ...current,
      advancedBeautify: {
        ...current.advancedBeautify,
        slides: reordered.map((slide, index) => ({ ...slide, index: index + 1 })),
      },
    } : current);
    try {
      const next = await userApi<AdvancedBeautifyResponse>(
        `/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/advanced-beautify/reorder`,
        { method: "POST", body: JSON.stringify({ slideOrder }) },
      );
      cacheAdvancedBeautify(taskId, next);
      setData(next);
      revision.resetAll();
      await Promise.all([loadProjectDetail(), revision.reloadMessages()]);
    } catch (caught) {
      setData(previousData);
      setSelectedIndex(selectedIndex);
      setError(caught instanceof Error ? caught.message : "页面顺序保存失败");
      setErrorCode(caught instanceof ApiError ? caught.code || "" : "");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
      setDraggedSlideIndex(undefined);
      setDragOverSlideIndex(undefined);
    }
  };

  const cancel = async () => {
    if (busy) return;
    setBusy("cancel");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/cancel-image2`, { method: "POST", body: "{}" });
      await loadStatus();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "停止高级美化失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  if (!data && error) {
    return <main className="advanced-beautify-empty">
      <strong>{errorCode === "feature_not_available" ? "当前账号尚未开通高级美化" : "高级美化暂时不可用"}</strong>
      <p>{errorCode === "feature_not_available" ? "高级美化当前未向此账户开放，请刷新账户权限后重试。" : error}</p>
      {requestId && <code>排查编号：{requestId}</code>}
      <button type="button" onClick={() => navigate(`/app/projects/${encodeURIComponent(taskId)}/generating`)}>返回标准模式</button>
    </main>;
  }

  if (!data) {
    return <main className="advanced-beautify-loading" role="status" aria-live="polite">
      <IconLoader2 size={34}/>
      <strong>正在恢复高级美化内容</strong>
      <p>正在确认当前任务的生成记录，请稍候…</p>
    </main>;
  }

  if (status === "idle" && !data.advancedBeautify.renderId) {
    return <AdvancedBeautifySetup
      taskId={taskId}
      title={detail?.task.title || data.task.title || "高级美化"}
      totalSlides={data.advancedBeautify.totalSlides || detail?.deckVersion?.document.slides.length || 0}
      busy={busy === "start"}
      error={imageModeAvailable ? error : "高级美化当前未向此账户开放"}
      errorCode={imageModeAvailable ? errorCode : "feature_not_available"}
      requestId={requestId}
      available={imageModeAvailable}
      beautifyMode={beautifyMode}
      themeCode={themeCode}
      standardPreviewUrl={data.standardPreviewUrl}
      standardPptxReady={data.standardPptxReady === true}
      onBeautifyModeChange={setBeautifyMode}
      onThemeCodeChange={setThemeCode}
      onBack={() => navigate(`/app/projects/${encodeURIComponent(taskId)}/generating`)}
      onStart={(frame) => void start({ action: "initial", standardPreviewFrame: frame })}
    />;
  }

  return <div className="advanced-beautify-page">
    <header className="standard-generate-topbar">
      <div className="standard-generate-titlebar">
        <button type="button" onClick={() => navigate("/app")} aria-label="返回工作台"><IconHome size={19}/></button>
        <h1>{detail?.task.title || data?.task.title || "高级美化"}</h1>
        <span className="ai-generated-label" aria-label="AI生成标识">AI生成</span>
        <span>{detail?.task.updatedAt ? `已保存 ${formatAdvancedHeaderTime(detail.task.updatedAt)}` : statusLabel(status)}</span>
        <IconCloudCheck size={20}/>
      </div>
      <GenerationModeTabs taskId={taskId} active="advanced" advancedEnabled={imageModeAvailable}/>
      <div className="standard-generate-actions">
        <button type="button" disabled={!data?.advancedBeautify.previewUrl} onClick={() => data?.advancedBeautify.previewUrl && window.open(data.advancedBeautify.previewUrl, "_blank", "noopener")}><IconPlayerPlay size={18}/>预览播放</button>
        <button type="button" className="primary" disabled={!data?.advancedBeautify.downloadUrl} onClick={() => data?.advancedBeautify.downloadUrl && void downloadPptxUrl(data.advancedBeautify.downloadUrl, `${detail?.task.title || data?.task.title || "advanced-beautify"}.pptx`).catch((caught) => setError(caught instanceof Error ? caught.message : "PPTX 下载失败"))}><IconDownload size={18}/>导出PPT</button>
      </div>
    </header>

    <div className="advanced-beautify-workspace">
      <aside className={`advanced-beautify-rail${busy === "reorder" ? " is-reordering" : ""}`} aria-label="幻灯片缩略图，可拖动调整顺序">
        {pageList.map((slide, index) => <button
          type="button"
          key={slide.sourceSlideId || `${slide.index}-${slide.title}`}
          className={`${index === selectedIndex ? "active" : ""}${draggedSlideIndex === index ? " is-dragging" : ""}${dragOverSlideIndex === index && draggedSlideIndex !== index ? ` is-drag-over ${draggedSlideIndex !== undefined && draggedSlideIndex < index ? "drop-after" : "drop-before"}` : ""}`}
          draggable={status === "succeeded" && !busy}
          disabled={busy === "reorder"}
          aria-label={`第 ${slide.index} 页：${slide.title}。拖动可调整顺序，或按 Alt 加上下方向键移动。`}
          onClick={() => selectSlide(index)}
          onKeyDown={(event) => {
            if (!event.altKey || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
            event.preventDefault();
            const target = event.key === "ArrowUp" ? index - 1 : index + 1;
            if (target >= 0 && target < pageList.length) void reorderSlide(index, target);
          }}
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", String(index));
            setDraggedSlideIndex(index);
            setDragOverSlideIndex(index);
          }}
          onDragOver={(event) => {
            if (draggedSlideIndex === undefined) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            setDragOverSlideIndex(index);
          }}
          onDrop={(event) => {
            event.preventDefault();
            const from = draggedSlideIndex ?? Number(event.dataTransfer.getData("text/plain"));
            if (Number.isInteger(from)) void reorderSlide(from, index);
          }}
          onDragEnd={() => {
            setDraggedSlideIndex(undefined);
            setDragOverSlideIndex(undefined);
          }}
        >
          <IconGripVertical className="advanced-beautify-drag-handle" size={14}/>
          <span>{String(slide.index).padStart(2, "0")}</span>
          <div>{slide.thumbnailUrl || slide.imageUrl || (slide.status === "succeeded" && slide.fallbackThumbnailUrl)
            ? <img draggable={false} src={slide.thumbnailUrl || slide.imageUrl || slide.fallbackThumbnailUrl} alt={slide.title}/>
            : <i>{slide.status === "running" ? `正在生成第 ${slide.index} 页` : slide.title}</i>}</div>
          <b className={slide.status}>{slideStatusIcon(slide.status)}</b>
        </button>)}
      </aside>

      <main className="advanced-beautify-stage-wrap">
        <section className="advanced-beautify-stage" ref={stageRef}>
          {previewImage ? <AdvancedBeautifyAnnotation
            imageSrc={previewImage}
            alt={selectedSlide?.title || "高级美化预览"}
            regions={selectedRegions}
            drawing={revision.annotationMode && selectedRegions.length < MAX_ADVANCED_BEAUTIFY_REGIONS}
            disabled={!revision.canEdit}
            onAddRegion={revision.addRegion}
            onRemoveRegion={revision.removeRegion}
          /> : <div className="advanced-beautify-placeholder" role={canPoll ? "status" : undefined} aria-live={canPoll ? "polite" : undefined}>
            {canPoll ? <IconLoader2 className="is-loading" size={42}/> : <IconSparkles size={42}/>}
            <strong>{canPoll ? `正在准备第 ${selectedSlide?.index || 1} 页` : selectedSlide?.title || selectedDeckSlide?.title || "等待生成"}</strong>
            <p>{selectedDeckSlide?.coreMessage || userFacingGenerationMessage(data?.task.progressText, "正在准备高级美化预览")}</p>
          </div>}
          {selectedSlide?.status === "running" && <div className="advanced-beautify-overlay" role="status" aria-live="polite"><IconLoader2 size={24}/><span>正在高级美化第 {selectedSlide.index} 页</span></div>}
        </section>
        <div className="advanced-beautify-toolbar">
          <button type="button" onClick={() => { userSelectedSlide.current = true; setSelectedIndex((value) => Math.max(0, value - 1)); }}>上一页</button>
          <strong>{selectedSlide?.index || 1} / {Math.max(1, pageList.length)}</strong>
          <button type="button" onClick={() => { userSelectedSlide.current = true; setSelectedIndex((value) => Math.min(pageList.length - 1, value + 1)); }}>下一页</button>
          <button
            type="button"
            className={`icon-only ${revision.annotationMode ? "active" : ""}`}
            disabled={!previewImage || status !== "succeeded" || revision.processing}
            aria-label={revision.annotationMode ? "退出区域选择" : "开启区域选择"}
            aria-pressed={revision.annotationMode}
            aria-describedby="advanced-beautify-annotation-help"
            onClick={() => revision.setAnnotationMode((value) => !value)}
          ><IconPencil size={16}/></button>
          <button type="button" disabled={!selectedRegions.length || revision.processing} onClick={revision.clearRegions} aria-label="清空当前页画框"><IconTrash size={16}/></button>
          <button type="button" aria-label="全屏预览" onClick={() => void stageRef.current?.requestFullscreen()}><IconArrowsMaximize size={18}/></button>
        </div>
        <p id="advanced-beautify-annotation-help" className="sr-only" aria-live="polite">
          {revision.annotationMode ? `框选模式已开启，当前页已有 ${selectedRegions.length} 个框。请在图片内拖动创建矩形。` : `当前页已有 ${selectedRegions.length} 个定位框。`}
        </p>
      </main>

      <aside className="advanced-beautify-panel existing-ppt-chat-panel">
        <AdvancedBeautifyChat
          selectedPageNumber={selectedPageNumber}
          disabled={status !== "succeeded"}
          regionCount={selectedRegions.length}
          value={revision.draft}
          job={revision.job}
          message={revision.feedback}
          messages={revision.messages}
          missingSlideGeneration={missingSlideGeneration?.step === "confirming" ? undefined : missingSlideGeneration}
          missingSlideStatus={missingSlideStatus}
          status={status}
          progressPercent={progressPercent}
          currentSlide={data?.advancedBeautify.currentSlide || selectedSlide?.index || 1}
          completedSlides={data?.advancedBeautify.completedSlides || 0}
          totalSlides={data?.advancedBeautify.totalSlides || pageList.length || 0}
          progressText={userFacingGenerationMessage(data?.task.progressText, statusLabel(status))}
          generationError={userFacingGenerationMessage(error || data?.advancedBeautify.errorMessage)}
          requestId={requestId}
          canPoll={canPoll}
          canResume={canResume}
          busy={busy}
          onChange={revision.setDraft}
          onSubmit={() => void revision.submit()}
          onRollback={(message) => void revision.rollback(message)}
          onCancel={() => void cancel()}
          onResume={() => void start({ action: "resume" })}
        />
      </aside>
    </div>
    {missingSlideGeneration?.step === "confirming" && <MissingSlideGenerationDialog
      flow={missingSlideGeneration}
      onCancel={cancelMissingSlideGeneration}
      onConfirm={confirmMissingSlideGeneration}
    />}
  </div>;
}

function AdvancedBeautifySetup({
  taskId,
  title,
  totalSlides,
  busy,
  error,
  errorCode,
  requestId,
  available,
  beautifyMode,
  themeCode,
  standardPreviewUrl,
  standardPptxReady,
  onBeautifyModeChange,
  onThemeCodeChange,
  onBack,
  onStart,
}: {
  taskId: string;
  title: string;
  totalSlides: number;
  busy: boolean;
  error: string;
  errorCode: string;
  requestId: string;
  available: boolean;
  beautifyMode: AdvancedBeautifyMode;
  themeCode: AdvancedBeautifyThemeCode;
  standardPreviewUrl?: string;
  standardPptxReady: boolean;
  onBeautifyModeChange: (mode: AdvancedBeautifyMode) => void;
  onThemeCodeChange: (theme: AdvancedBeautifyThemeCode) => void;
  onBack: () => void;
  onStart: (standardPreviewFrame?: HTMLIFrameElement | null) => void;
}) {
  const standardPreviewFrame = useRef<HTMLIFrameElement>(null);
  return <div className="advanced-beautify-page">
    <header className="standard-generate-topbar">
      <div className="standard-generate-titlebar">
        <button type="button" onClick={onBack} aria-label="返回标准结果页"><IconHome size={19}/></button>
        <h1>{title}</h1>
        <span className="ai-generated-label" aria-label="AI生成标识">AI生成</span>
        <span>高级美化设置</span>
        <IconCloudCheck size={20}/>
      </div>
      <GenerationModeTabs taskId={taskId} active="advanced" advancedEnabled={available}/>
      <div className="standard-generate-actions"/>
    </header>

    <main className="advanced-beautify-setup">
      <section className="advanced-beautify-setup-copy">
        <span className="advanced-beautify-setup-kicker"><IconSparkles size={17}/>高级美化模式</span>
        <h2>把每一页变成完整的视觉画布</h2>
        <p>高级美化会在保留原有内容逻辑的基础上，重新组织页面的视觉表达。生成后无需手动编辑页面元素，直接通过对话框描述修改需求即可。</p>
        <div className="advanced-beautify-benefits">
          <article><IconCheck size={18}/><div><strong>完整视觉画布</strong><span>每页按整体画面重新设计，不再是可拆分的零散元素。</span></div></article>
          <article><IconCheck size={18}/><div><strong>保留内容与页数</strong><span>沿用当前标准版的内容结构，便于继续审核与交付。</span></div></article>
          <article><IconCheck size={18}/><div><strong>通过对话沟通修改</strong><span>完成后选择页面，用自然语言描述你希望调整的内容。</span></div></article>
        </div>
      </section>

      <aside className="advanced-beautify-setup-panel">
        <AdvancedBeautifyPolicyPicker
          beautifyMode={beautifyMode}
          themeCode={themeCode}
          radioGroupId="setup"
          onBeautifyModeChange={onBeautifyModeChange}
          onThemeCodeChange={onThemeCodeChange}
        />
        <div className="advanced-beautify-setup-summary"><span>即将转换</span><strong>{totalSlides} 页标准版内容</strong></div>
        {error ? errorCode === "feature_not_available"
          ? <div className="advanced-beautify-access-denied">
              <IconAlertCircle size={21}/>
              <div><strong>当前账号尚未开通高级美化</strong><p>高级美化当前未向此账户开放，请刷新账户权限后重试。</p>{requestId && <code>排查编号：{requestId}</code>}</div>
            </div>
          : <div className="advanced-beautify-error"><IconAlertCircle size={18}/><span>{error}</span>{requestId && <code>排查编号：{requestId}</code>}</div>
          : null}
        <div className="advanced-beautify-setup-actions">
          <button type="button" className={`advanced-beautify-start${busy ? " is-loading" : ""}`} disabled={busy || !available} onClick={() => onStart(standardPreviewFrame.current)}>
            {busy ? <IconLoader2 size={19}/> : <IconSparkles size={19}/>}{busy ? "正在启动…" : "开始高级美化"}
          </button>
          <p className="advanced-beautify-setup-note">开始后将逐页生成。你可以留在此页查看进度，也可以稍后回来继续查看。</p>
        </div>
        {advancedBeautifyRequiresStandardPptx(beautifyMode) && !standardPptxReady && standardPreviewUrl && <iframe
          ref={standardPreviewFrame}
          className="advanced-beautify-standard-preloader"
          src={standardPreviewUrl}
          title="标准模式页面准备"
          tabIndex={-1}
          aria-hidden="true"
        />}
      </aside>
    </main>
  </div>;
}

function AdvancedBeautifyPolicyPicker({
  beautifyMode,
  themeCode,
  radioGroupId,
  compact = false,
  disabled = false,
  onBeautifyModeChange,
  onThemeCodeChange,
}: {
  beautifyMode: AdvancedBeautifyMode;
  themeCode: AdvancedBeautifyThemeCode;
  radioGroupId: string;
  compact?: boolean;
  disabled?: boolean;
  onBeautifyModeChange: (mode: AdvancedBeautifyMode) => void;
  onThemeCodeChange: (theme: AdvancedBeautifyThemeCode) => void;
}) {
  return <div className={`advanced-beautify-policy-picker${compact ? " compact" : ""}`}>
    <AdvancedBeautifyModePicker
      beautifyMode={beautifyMode}
      compact={compact}
      disabled={disabled}
      onBeautifyModeChange={onBeautifyModeChange}
    />
    <AdvancedBeautifyThemePicker
      themeCode={themeCode}
      radioGroupId={radioGroupId}
      disabled={disabled}
      onThemeCodeChange={onThemeCodeChange}
    />
  </div>;
}

function AdvancedBeautifyModePicker({
  beautifyMode,
  compact = false,
  disabled = false,
  label = "美化方式",
  title = compact ? "选择重新生成方式" : "选择一个样式开始",
  onBeautifyModeChange,
}: {
  beautifyMode: AdvancedBeautifyMode;
  compact?: boolean;
  disabled?: boolean;
  label?: string;
  title?: string;
  onBeautifyModeChange: (mode: AdvancedBeautifyMode) => void;
}) {
  return <fieldset className="advanced-beautify-picker-section advanced-beautify-mode-picker" aria-label={label} disabled={disabled}>
    <div className="advanced-beautify-style-heading">
      <div><span>{label}</span><strong>{title}</strong></div>
      <small>影响内容与版式调整范围</small>
    </div>
    <div className="advanced-beautify-mode-options">
      {ADVANCED_BEAUTIFY_MODE_OPTIONS.map((option) => <button
        type="button"
        key={option.value}
        className={`advanced-beautify-style-option${beautifyMode === option.value ? " active" : ""}`}
        aria-pressed={beautifyMode === option.value}
        onClick={() => onBeautifyModeChange(option.value)}
      >
        <span className="advanced-beautify-style-icon"><IconSparkles size={compact ? 16 : 21}/></span>
        <span><strong>{option.name}</strong><em>{option.description}</em></span>
        {option.recommended && <b>推荐</b>}
      </button>)}
    </div>
  </fieldset>;
}

function AdvancedBeautifyThemePicker({
  themeCode,
  radioGroupId,
  disabled = false,
  onThemeCodeChange,
}: {
  themeCode: AdvancedBeautifyThemeCode;
  radioGroupId: string;
  disabled?: boolean;
  onThemeCodeChange: (theme: AdvancedBeautifyThemeCode) => void;
}) {
  const imageThemes = useImageThemeOptions();
  return <fieldset className="advanced-beautify-picker-section advanced-beautify-theme-picker" aria-label="选择视觉主题" disabled={disabled}>
    <div className="advanced-beautify-theme-heading">
      <div><span>视觉主题</span><strong>选择高级美化生图风格</strong></div>
      <small>{themeCode === "smart-detect" ? "自动检测行业风格" : "使用固定主题约束"}</small>
    </div>
    <div className="theme-option-grid advanced-beautify-theme-grid">
      {imageThemes.map((theme) => <label key={theme.key} className={themeCode === theme.key ? "selected" : ""}>
        <input
          type="radio"
          name={`advanced-beautify-theme-${radioGroupId}`}
          checked={themeCode === theme.key}
          onChange={() => onThemeCodeChange(theme.key as AdvancedBeautifyThemeCode)}
        />
        <span className={`theme-option-preview theme-option-${theme.previewThemePack}`}>
          <img src={THEME_PREVIEW_IMAGES[theme.previewThemePack]} alt=""/>
        </span>
        <span className="theme-option-copy"><b>{theme.name}</b><small>{theme.description}</small></span>
      </label>)}
    </div>
  </fieldset>;
}

function AdvancedBeautifyChat({
  selectedPageNumber,
  disabled,
  regionCount,
  value,
  job,
  message,
  messages,
  missingSlideGeneration,
  missingSlideStatus,
  status,
  progressPercent,
  currentSlide,
  completedSlides,
  totalSlides,
  progressText,
  generationError,
  requestId,
  canPoll,
  canResume,
  busy,
  onChange,
  onSubmit,
  onRollback,
  onCancel,
  onResume,
}: {
  selectedPageNumber: number;
  disabled: boolean;
  regionCount: number;
  value: string;
  job?: Image2RevisionJob;
  message?: Image2RevisionFeedback;
  messages: PptEditMessage[];
  missingSlideGeneration?: MissingSlideGenerationFlow;
  missingSlideStatus?: AdvancedBeautifySlideStatus;
  status: AdvancedBeautifyStatus;
  progressPercent: number;
  currentSlide: number;
  completedSlides: number;
  totalSlides: number;
  progressText: string;
  generationError: string;
  requestId: string;
  canPoll: boolean;
  canResume: boolean;
  busy: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onRollback: (message: PptEditMessage) => void;
  onCancel: () => void;
  onResume: () => void;
}) {
  const processing = Boolean(job);
  const historyRef = useRef<HTMLDivElement>(null);
  const visibleMessages = useMemo(() => advancedBeautifyVisibleMessages(messages), [messages]);
  const latestVersionMessageBySlide = latestRollbackVersionBySlide(visibleMessages);
  const generationTone = status === "succeeded" ? "success" : status === "failed" || status === "cancelled" ? "error" : "progress";
  const generationMessage = advancedBeautifyGenerationMessage({
    status,
    progressPercent,
    currentSlide,
    completedSlides,
    totalSlides,
    progressText,
  });
  useEffect(() => {
    const node = historyRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [generationMessage, job, message, missingSlideGeneration, missingSlideStatus, selectedPageNumber, visibleMessages]);

  return <section className="existing-ppt-chat advanced-beautify-chat" aria-label="高级美化对话框">
    <header className="existing-ppt-chat-head">
      <div className="existing-ppt-chat-identity">
        <span className="existing-ppt-chat-avatar"><IconSparkles size={18}/></span>
        <span><strong>AI 美化助手</strong><small>SYSTEM READY</small></span>
      </div>
      <p>{status === "succeeded"
        ? regionCount ? `已框选 ${regionCount} 个区域，请逐行填写要求` : `描述第 ${selectedPageNumber} 页的修改要求`
        : `整体生成进度 ${progressPercent}%`}</p>
    </header>
    <div className="existing-ppt-chat-history" ref={historyRef} aria-label="高级美化对话记录" aria-live="polite">
      {visibleMessages.map((item) => {
        const historicalResource = (item.messageType === "initial_version" || item.messageType === "revision" || item.messageType === "rollback")
          && Boolean(item.resource);
        const expired = historicalResource && (item.resourceExpired || item.resourceDeleted);
        return <article key={item.id} className={`existing-ppt-chat-message ${item.role} ${item.messageType}`}>
          <div>
            <span>{item.role === "user" ? "你" : "AI"}</span>
            {item.resource?.slideIndex && <small>第 {item.resource.slideIndex} 页</small>}
            <time>{formatEditMessageTime(item.createdAt)}</time>
          </div>
          <p>{userFacingGenerationMessage(item.contentText)}</p>
          {historicalResource && <footer>
            <span>{expired ? "资源已过期，记录仍保留" : `资源有效至 ${formatEditMessageExpiry(item.expiresAt)}`}</span>
            {item.canRollback && (item.messageType === "initial_version"
              || (item.resource?.slideIndex && latestVersionMessageBySlide.get(item.resource.slideIndex) === item.id))
              && <button type="button" disabled={disabled || processing} onClick={() => onRollback(item)}>
              <IconRefresh size={13}/>{item.messageType === "initial_version" ? "恢复首次版本" : "回退"}
            </button>}
          </footer>}
        </article>;
      })}
      <article className={`existing-ppt-chat-message assistant ${generationTone}`}>
        <div>
          <span>AI</span>
          {canPoll ? <IconLoader2 size={14}/> : status === "succeeded" ? <IconCheck size={14}/> : status === "failed" || status === "cancelled" ? <IconAlertCircle size={14}/> : null}
        </div>
        <p>{canPoll ? trimProgressPunctuation(generationMessage) : generationMessage}{canPoll && <span className="animated-ellipsis" aria-label="生成中">...</span>}</p>
        {(canPoll || canResume) && <footer className="advanced-beautify-generation-actions">
          <span>{canPoll ? "关闭页面不会影响后台生成" : "已完成页面将继续保留"}</span>
          <div>
            {canPoll && <button type="button" disabled={busy === "cancel"} onClick={onCancel}><IconX size={13}/>{busy === "cancel" ? "停止中" : "停止生成"}</button>}
            {canResume && <button type="button" className="primary" disabled={busy === "resume"} onClick={onResume}><IconLoader2 size={13}/>{busy === "resume" ? "继续中" : "继续生成"}</button>}
          </div>
        </footer>}
      </article>
      {missingSlideGeneration && <MissingSlideGenerationMessage
        flow={missingSlideGeneration}
        slideStatus={missingSlideStatus}
        canPoll={canPoll}
        progressText={progressText}
      />}
      {generationError && <article className="existing-ppt-chat-message assistant error terminal-error" role="alert">
        <div><span>系统</span><IconAlertCircle size={14}/></div>
        <p>{generationError}{requestId ? `\nRequest ID: ${requestId}` : ""}</p>
      </article>}
      {job && <article className="existing-ppt-chat-message assistant revision_progress">
        <div><span>AI</span><small>第 {selectedPageNumber} 页</small><IconLoader2 size={14}/></div>
        <p>{trimProgressPunctuation(`${revisionJobLabel(job.status, selectedPageNumber)}\n${revisionJobDescription(job.status)}`)}<span className="animated-ellipsis" aria-label="生成中">...</span></p>
      </article>}
      {message && <article className={`existing-ppt-chat-message assistant ${message.tone}`} role="status">
        <div><span>AI</span>{message.tone === "success" ? <IconCheck size={14}/> : <IconAlertCircle size={14}/>}</div>
        <p>{userFacingGenerationMessage(message.text)}</p>
      </article>}
    </div>
    <div className="ppt-chat-compose-card">
      <div className="existing-ppt-chat-input">
        <textarea
          id="advanced-beautify-instruction"
          value={value}
          disabled={disabled || processing}
          aria-label={`第 ${selectedPageNumber} 页重做要求`}
          aria-describedby="advanced-beautify-composer-hint"
          placeholder={processing
            ? "当前任务正在处理中…"
            : regionCount
              ? "按“框X：修改要求”逐行填写"
              : "描述你希望这页如何变化，例如：放大标题，突出核心数据"}
          onChange={(event) => onChange(limitAdvancedRevisionInstruction(event.target.value))}
          onKeyDown={(event) => {
            if (regionCount === 0 && event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSubmit();
            }
          }}
        />
      </div>
      <footer className="existing-ppt-chat-composer">
        <span className="ppt-chat-compose-count">{advancedRevisionInstructionLength(value)} / {ADVANCED_REVISION_INSTRUCTION_MAX_LENGTH}</span>
        <small id="advanced-beautify-composer-hint" className="ppt-chat-compose-hint">
          {regionCount ? "每个框选区域单独一行" : "Enter 发送 · Shift+Enter 换行"}
        </small>
        <button type="button" disabled={disabled || processing || (regionCount === 0 && !advancedRevisionInstructionValid(value))} aria-label="发送美化要求" title="发送美化要求" onClick={onSubmit}><IconArrowUp size={19}/></button>
      </footer>
    </div>
  </section>;
}

function MissingSlideGenerationMessage({
  flow,
  slideStatus,
  canPoll,
  progressText,
}: {
  flow: MissingSlideGenerationFlow;
  slideStatus?: AdvancedBeautifySlideStatus;
  canPoll: boolean;
  progressText: string;
}) {
  const selectedMode = ADVANCED_BEAUTIFY_MODE_OPTIONS.find((option) => option.value === flow.beautifyMode)?.name || flow.beautifyMode;
  const selectedTheme = useImageThemeOptions().find((theme) => theme.key === flow.themeCode)?.name || flow.themeCode;
  const completed = slideStatus === "succeeded";
  const failed = flow.step === "error"
    || (flow.step === "submitted" && !canPoll && slideStatus === "failed");
  const tone = failed ? "error" : completed ? "success" : "revision_progress";
  let content = `正在提交第 ${flow.slideIndex} 页图片生成请求，将沿用“${selectedTheme} / ${selectedMode}”。`;
  if (flow.step === "error") {
    content = `第 ${flow.slideIndex} 页图片生成请求提交失败：${userFacingGenerationMessage(flow.error, "请稍后重试")}`;
  } else if (completed) {
    content = `第 ${flow.slideIndex} 页图片已生成完成，沿用了“${selectedTheme} / ${selectedMode}”。`;
  } else if (flow.step === "submitted") {
    content = canPoll
      ? `第 ${flow.slideIndex} 页已加入生成流程，将沿用“${selectedTheme} / ${selectedMode}”。\n${progressText}`
      : `第 ${flow.slideIndex} 页已提交生成，将沿用“${selectedTheme} / ${selectedMode}”。`;
  }
  return <article className={`existing-ppt-chat-message assistant ${tone}`} role={failed ? "alert" : "status"}>
    <div>
      <span>AI</span>
      <small>第 {flow.slideIndex} 页</small>
      {failed ? <IconAlertCircle size={14}/> : completed ? <IconCheck size={14}/> : <IconLoader2 size={14}/>}
    </div>
    <p>
      {content}
      {failed && flow.requestId && <><br/><small>Request ID: {flow.requestId}</small></>}
    </p>
  </article>;
}

function MissingSlideGenerationDialog({
  flow,
  onCancel,
  onConfirm,
}: {
  flow: MissingSlideGenerationFlow;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const selectedMode = ADVANCED_BEAUTIFY_MODE_OPTIONS.find((option) => option.value === flow.beautifyMode)?.name || flow.beautifyMode;
  const selectedTheme = useImageThemeOptions().find((theme) => theme.key === flow.themeCode)?.name || flow.themeCode;
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onCancel]);
  return <div
    className="standard-delete-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="advanced-missing-slide-title"
    aria-describedby="advanced-missing-slide-description"
  >
    <section className="standard-delete-dialog standard-retry-dialog">
      <span><IconSparkles size={24}/></span>
      <h2 id="advanced-missing-slide-title">是否生成第 {flow.slideIndex} 页图片？</h2>
      <p id="advanced-missing-slide-description">
        “{flow.slideTitle}”尚未生成高级美化图片。确认后将沿用“{selectedTheme}”主题和“{selectedMode}”方式继续生成，已完成页面不会重复生成。
      </p>
      <div>
        <button type="button" autoFocus onClick={onCancel}>取消</button>
        <button type="button" className="primary" onClick={onConfirm}>生成图片</button>
      </div>
    </section>
  </div>;
}

function advancedBeautifyGenerationMessage({
  status,
  progressPercent,
  currentSlide,
  completedSlides,
  totalSlides,
  progressText,
}: {
  status: AdvancedBeautifyStatus;
  progressPercent: number;
  currentSlide: number;
  completedSlides: number;
  totalSlides: number;
  progressText: string;
}): string {
  const progress = `已完成 ${completedSlides}/${totalSlides} 页，当前第 ${currentSlide} 页，整体进度 ${progressPercent}%。`;
  if (status === "succeeded") return `高级美化已完成，共生成 ${totalSlides} 页。\n现在可以选择任意页面并发送修改或重新生成要求。`;
  if (status === "failed") return `高级美化未完成。\n${progressText || progress}`;
  if (status === "cancelled") return `高级美化已停止。\n${progress}\n已完成页面和项目内容仍会保留。`;
  if (status === "queued") return `高级美化任务已进入队列。\n${progress}\n${progressText}`;
  return `正在进行高级美化。\n${progress}\n${progressText}`;
}

function formatAdvancedHeaderTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "--:--";
  return date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" });
}

function formatEditMessageTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function trimProgressPunctuation(value: string): string {
  return value.replace(/[。.!！…\s]+$/u, "");
}

function formatEditMessageExpiry(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "未知";
  return date.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function revisionJobLabel(status: Image2RevisionJobStatus, page: number) {
  if (status === "validating") return `正在检查第 ${page} 页修改要求`;
  if (status === "submitting") return `第 ${page} 页正在提交重做任务`;
  if (status === "generating") return `正在重做并应用第 ${page} 页`;
  return `正在回退第 ${page} 页`;
}

function revisionJobDescription(status: Image2RevisionJobStatus) {
  if (status === "validating") return "确认要求是否可用于重新生成当前页面图片。";
  if (status === "submitting") return "正在创建 Worker 任务，原页面保持不变。";
  if (status === "generating") return "理解重做要求，生成后直接替换当前页并保留修改前版本。";
  return "正在恢复上一个页面版本并重建 PPTX。";
}

export function chooseAdvancedBeautifyDisplayIndex(input: {
  previousIndex: number;
  slides: Array<{ index: number; status: AdvancedBeautifySlideStatus; imageUrl?: string; thumbnailUrl?: string }>;
  status: AdvancedBeautifyStatus;
  currentSlide?: number;
  userSelected: boolean;
}): number {
  if (!input.slides.length) return 0;
  const maxIndex = input.slides.length - 1;
  const previous = Math.max(0, Math.min(input.previousIndex, maxIndex));
  if (input.userSelected) return previous;
  if (input.status === "queued" || input.status === "running") {
    const latestGenerated = [...input.slides].reverse().find((slide) =>
      slide.status === "succeeded" && Boolean(slide.imageUrl || slide.thumbnailUrl)
    );
    if (latestGenerated) return Math.max(0, Math.min(maxIndex, latestGenerated.index - 1));
    if (input.currentSlide) return Math.max(0, Math.min(maxIndex, input.currentSlide - 1));
  }
  return previous;
}

export function shouldShowAdvancedBeautifyResume(input: { status: AdvancedBeautifyStatus; completedSlides: number; totalSlides: number }): boolean {
  return ["failed", "cancelled"].includes(input.status) && input.totalSlides > 0;
}

export function advancedBeautifyRequiresStandardPptx(mode: AdvancedBeautifyMode): boolean {
  return mode === "faithful";
}

function statusLabel(status: AdvancedBeautifyStatus) {
  return ({ idle: "等待确认", queued: "排队中", running: "生成中", succeeded: "已完成", failed: "失败", cancelled: "已停止" } as Record<AdvancedBeautifyStatus, string>)[status];
}

function slideStatusLabel(status: AdvancedBeautifySlideStatus) {
  return ({ waiting: "待生成", running: "生成中", succeeded: "已完成", failed: "失败" } as Record<AdvancedBeautifySlideStatus, string>)[status];
}

function slideStatusIcon(status: AdvancedBeautifySlideStatus) {
  if (status === "succeeded") return <IconCheck size={15}/>;
  if (status === "running") return <IconLoader2 size={15}/>;
  if (status === "failed") return <IconAlertCircle size={15}/>;
  return <span/>;
}

function loadSessionProjectDetail(taskId: string): ProjectDetailResponse | undefined {
  try {
    const value = JSON.parse(sessionStorage.getItem(`pptdeep:task:${taskId}`) || "null") as ProjectDetailResponse | undefined;
    return value?.ok ? value : undefined;
  } catch {
    return undefined;
  }
}

function cacheSessionProjectDetail(taskId: string, value: ProjectDetailResponse) {
  try {
    sessionStorage.setItem(`pptdeep:task:${taskId}`, JSON.stringify(value));
  } catch {
    // The live React state remains authoritative when session storage is unavailable or full.
  }
}

function advancedBeautifyCacheKey(taskId: string) {
  return `pptdeep:advanced-beautify:v1:${taskId}`;
}

function loadCachedAdvancedBeautify(taskId: string): AdvancedBeautifyResponse | undefined {
  const now = Date.now();
  const memoryEntry = advancedBeautifyMemoryCache.get(taskId);
  if (memoryEntry && now - memoryEntry.savedAt <= ADVANCED_BEAUTIFY_CACHE_TTL_MS) return memoryEntry.value;
  if (memoryEntry) advancedBeautifyMemoryCache.delete(taskId);
  try {
    const entry = JSON.parse(sessionStorage.getItem(advancedBeautifyCacheKey(taskId)) || "null") as { savedAt?: number; value?: AdvancedBeautifyResponse } | null;
    if (!entry?.savedAt || !entry.value?.ok || entry.value.task.id !== taskId || now - entry.savedAt > ADVANCED_BEAUTIFY_CACHE_TTL_MS) {
      sessionStorage.removeItem(advancedBeautifyCacheKey(taskId));
      return undefined;
    }
    advancedBeautifyMemoryCache.set(taskId, { savedAt: entry.savedAt, value: entry.value });
    return entry.value;
  } catch {
    return undefined;
  }
}

function cacheAdvancedBeautify(taskId: string, value: AdvancedBeautifyResponse) {
  if (!value.ok || value.task.id !== taskId) return;
  const entry = { savedAt: Date.now(), value };
  advancedBeautifyMemoryCache.set(taskId, entry);
  try {
    sessionStorage.setItem(advancedBeautifyCacheKey(taskId), JSON.stringify(entry));
  } catch {
    // Keep the in-memory entry; switching modes still remains instant in this tab.
  }
}
